/*!
 * vuex-connect v2.1.0
 * https://github.com/ktsn/vuex-connect
 *
 * Copyright (c) 2016-present katashin
 * Released under the MIT license
 * https://github.com/ktsn/vuex-connect/blob/master/LICENSE
 */
import Vue from 'vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

function merge() {
  var target = {};

  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  args.forEach(function (obj) {
    Object.keys(obj).forEach(function (key) {
      target[key] = obj[key];
    });
  });
  return target;
}

function pick(obj, keys) {
  var res = {};
  keys.forEach(function (key) {
    if (obj[key] !== void 0) {
      res[key] = obj[key];
    }
  });
  return res;
}

function omit(obj, keys) {
  var res = {};
  Object.keys(obj).forEach(function (key) {
    if (!includes(keys, key)) {
      res[key] = obj[key];
    }
  });
  return res;
}

function flattenObject(obj) {
  return Object.keys(obj).reduce(function (acc, key) {
    return acc.concat(obj[key]);
  }, []);
}

function mapValues(obj, f) {
  var res = {};
  Object.keys(obj).forEach(function (key) {
    res[key] = f(obj[key], key);
  });
  return res;
}

function keys() {
  return Object.keys(merge.apply(undefined, arguments));
}

function includes(array, item) {
  return array.indexOf(item) > -1;
}

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var LIFECYCLE_KEYS = ['beforeCreate', 'created', 'beforeMount', 'mounted', 'beforeUpdate', 'updated', 'activated', 'deactivated', 'beforeDestroy', 'destroyed'];

var createConnect = function createConnect(transform) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var _mapValues = mapValues(options, normalizeOptions);

    var _mapValues$stateToPro = _mapValues.stateToProps;
    var stateToProps = _mapValues$stateToPro === undefined ? {} : _mapValues$stateToPro;
    var _mapValues$gettersToP = _mapValues.gettersToProps;
    var gettersToProps = _mapValues$gettersToP === undefined ? {} : _mapValues$gettersToP;
    var _mapValues$actionsToP = _mapValues.actionsToProps;
    var actionsToProps = _mapValues$actionsToP === undefined ? {} : _mapValues$actionsToP;
    var _mapValues$actionsToE = _mapValues.actionsToEvents;
    var actionsToEvents = _mapValues$actionsToE === undefined ? {} : _mapValues$actionsToE;
    var _mapValues$mutationsT = _mapValues.mutationsToProps;
    var mutationsToProps = _mapValues$mutationsT === undefined ? {} : _mapValues$mutationsT;
    var _mapValues$mutationsT2 = _mapValues.mutationsToEvents;
    var mutationsToEvents = _mapValues$mutationsT2 === undefined ? {} : _mapValues$mutationsT2;
    var _mapValues$methodsToP = _mapValues.methodsToProps;
    var methodsToProps = _mapValues$methodsToP === undefined ? {} : _mapValues$methodsToP;
    var _mapValues$methodsToE = _mapValues.methodsToEvents;
    var methodsToEvents = _mapValues$methodsToE === undefined ? {} : _mapValues$methodsToE;
    var _mapValues$lifecycle = _mapValues.lifecycle;
    var lifecycle = _mapValues$lifecycle === undefined ? {} : _mapValues$lifecycle;


    return function (name, Component) {
      if (typeof name !== 'string') {
        Component = name;
        name = getOptions(Component).name || 'wrapped-anonymous-component';
      }

      var propKeys = keys(stateToProps, gettersToProps, actionsToProps, mutationsToProps, methodsToProps);

      var eventKeys = keys(actionsToEvents, mutationsToEvents, methodsToEvents);

      var originalProps = getOptions(Component).props || {};

      var containerProps = void 0,
          containerPropsKeys = void 0;
      if (Array.isArray(originalProps)) {
        containerProps = containerPropsKeys = originalProps.filter(function (p) {
          return propKeys.indexOf(p) < 0;
        });
      } else {
        containerProps = omit(originalProps, propKeys);
        containerPropsKeys = Object.keys(containerProps);
      }

      var options = {
        name: 'connect-' + name,
        props: containerProps,
        components: defineProperty({}, name, Component),
        computed: merge(mapState(stateToProps), mapGetters(gettersToProps)),
        methods: merge(mapActions(merge(actionsToProps, actionsToEvents)), mapMutations(merge(mutationsToProps, mutationsToEvents)), mapValues(merge(methodsToProps, methodsToEvents), bindStore))
      };

      insertLifecycleMixin(options, lifecycle);
      insertRenderer(options, name, propKeys.concat(containerPropsKeys), eventKeys);

      if (transform) {
        transform(options, lifecycle);
      }

      return typeof Component === 'function' ? Vue.extend(options) : options;
    };
  };
};

function insertRenderer(options, name, propKeys, eventKeys) {
  options.render = function (h) {
    var _this = this;

    return h(name, {
      props: pick(this, propKeys),
      on: mergeListeners(pick(this, eventKeys), this.$listeners || {}),
      scopedSlots: this.$scopedSlots
    }, flattenObject(this.$slots).map(function (slot) {
      return patchSlot(slot, _this);
    }));
  };
}

function insertLifecycleMixin(options, lifecycle) {
  options.mixins = [mapValues(pick(lifecycle, LIFECYCLE_KEYS), function (f) {
    return function boundLifecycle() {
      f.call(this, this.$store);
    };
  })];
}

function mergeListeners(a, b) {
  Object.keys(b).forEach(function (key) {
    if (a.hasOwnProperty(key)) {
      a[key] = [].concat(a[key]).concat(b[key]);
    } else {
      a[key] = b[key];
    }
  });
  return a;
}

function getOptions(Component) {
  if (typeof Component === 'function') {
    return Component.options;
  }
  return Component;
}

function bindStore(fn) {
  return function boundFunctionWithStore() {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return fn.call.apply(fn, [this, this.$store].concat(args));
  };
}

function normalizeOptions(options) {
  return Array.isArray(options) ? options.reduce(function (obj, value) {
    obj[value] = value;
    return obj;
  }, {}) : options;
}

/**
 * Modify the slot context to let Vue handle
 * named slot expectedly
 */
function patchSlot(vnode, vm) {
  if (vnode.context) {
    vnode.context = vm._self;
  }
  if (vnode.fnContext) {
    vnode.fnContext = vm._self;
  }
  return vnode;
}

var connect = createConnect();

export { connect, createConnect };
